import { create } from 'zustand';

interface Modifier {
  id: string;
  name: string;
  priceCents: number;
}

interface OrderItem {
  id: string;
  menuItemId: string;
  name: string;
  shortName?: string;
  priceCents: number;
  quantity: number;
  modifiers: Modifier[];
  specialInstructions?: string;
  seatNumber?: number;
  courseNumber?: number;
  // Server status (only present when loaded from existing check)
  holdUntilFired?: boolean;
  status?: string;
  kitchenStatus?: string;
}

interface OrderState {
  tableId: string | null;
  tableNumber: string | null;
  checkId: string | null;
  guestId: string | null;
  guestName: string | null;
  items: OrderItem[];
  partySize: number;
  currentSeat: number;
  currentCourse: number;
  taxRate: number; // Tax rate as decimal (e.g., 0.0875 for 8.75%)

  // Actions
  setTable: (tableId: string, tableNumber: string) => void;
  setCheckId: (checkId: string | null) => void;
  setGuest: (guestId: string | null, guestName: string | null) => void;
  setPartySize: (size: number) => void;
  setCurrentSeat: (seat: number) => void;
  setCurrentCourse: (course: number) => void;
  setTaxRate: (rate: number) => void;

  addItem: (item: Omit<OrderItem, 'id' | 'quantity'>) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string) => void;
  updateItemModifiers: (itemId: string, modifiers: Modifier[]) => void;
  updateItemInstructions: (itemId: string, instructions: string) => void;
  updateItemCourse: (itemId: string, courseNumber: number) => void;
  loadItems: (items: OrderItem[]) => void;
  getItemsByCourse: () => Map<number, OrderItem[]>;
  getMaxCourse: () => number;

  getSubtotal: () => number;
  getTax: () => number;
  getTotal: () => number;

  clearItems: () => void;
  clearOrder: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

/**
 * Map server check orders to local OrderItems — the ONE place server item
 * shapes are converted, so every view (cart, By Course, Checkout) loads
 * identically. Two server-shape gotchas handled here:
 * - server unitPriceCents already INCLUDES modifier adjustments, while
 *   getSubtotal adds modifier prices on top → store the base price
 *   (unit − modifiers) so totals match the server exactly.
 * - server modifiers carry `priceAdjustment` (not `priceCents`); mapping them
 *   verbatim made subtotals NaN.
 */
export function mapServerOrderItems(orders: any[]): OrderItem[] {
  return (orders || []).flatMap((order: any) =>
    (order.items || [])
      .filter((item: any) => !item.isVoided)
      .map((item: any) => {
        const modifiers: Modifier[] = (item.modifiers || []).map((m: any) => ({
          id: m.modifierId || m.id || generateId(),
          name: m.name || '',
          priceCents: (m.priceAdjustment ?? m.priceCents ?? 0) * (m.quantity || 1),
        }));
        const modifiersTotal = modifiers.reduce((s, m) => s + m.priceCents, 0);
        const unitPrice = item.unitPriceCents ?? item.priceCents ?? 0;
        return {
          id: item.id,
          menuItemId: item.menuItemId,
          name: item.name,
          shortName: item.shortName,
          priceCents: Math.max(unitPrice - modifiersTotal, 0),
          quantity: item.quantity || 1,
          modifiers,
          specialInstructions: item.specialInstructions,
          seatNumber: item.seatNumber,
          courseNumber: item.courseNumber,
          holdUntilFired: item.holdUntilFired,
          status: item.status,
          kitchenStatus: item.kitchenStatus,
        };
      })
  );
}

export const useOrderStore = create<OrderState>((set, get) => ({
  tableId: null,
  tableNumber: null,
  checkId: null,
  guestId: null,
  guestName: null,
  items: [],
  partySize: 1,
  currentSeat: 1,
  currentCourse: 1,
  taxRate: 0.0875, // Default 8.75%, updated from restaurant settings on login

  setTable: (tableId, tableNumber) => {
    const prev = get().tableId;
    if (prev && prev !== tableId) {
      // Switching tables resets ALL per-check state — carrying the previous
      // cart along made "the same order show for every table" (and blocked
      // the server load, which only runs when the cart is empty)
      set({
        tableId,
        tableNumber,
        checkId: null,
        guestId: null,
        guestName: null,
        items: [],
        partySize: 1,
        currentSeat: 1,
        currentCourse: 1,
      });
    } else {
      set({ tableId, tableNumber });
    }
  },
  setCheckId: (checkId) => set({ checkId }),
  setGuest: (guestId, guestName) => set({ guestId, guestName }),
  setPartySize: (partySize) => set({ partySize }),
  setCurrentSeat: (currentSeat) => set({ currentSeat }),
  setCurrentCourse: (currentCourse) => set({ currentCourse }),
  setTaxRate: (taxRate) => set({ taxRate }),

  addItem: (item) => {
    const state = get();
    const existingItem = state.items.find(
      (i) =>
        i.menuItemId === item.menuItemId &&
        i.seatNumber === (item.seatNumber || state.currentSeat) &&
        JSON.stringify(i.modifiers) === JSON.stringify(item.modifiers) &&
        i.specialInstructions === item.specialInstructions
    );

    if (existingItem) {
      set({
        items: state.items.map((i) =>
          i.id === existingItem.id ? { ...i, quantity: i.quantity + 1 } : i
        ),
      });
    } else {
      set({
        items: [
          ...state.items,
          {
            ...item,
            id: generateId(),
            quantity: 1,
            seatNumber: item.seatNumber || state.currentSeat,
            courseNumber: item.courseNumber || state.currentCourse,
          },
        ],
      });
    }
  },

  updateItemQuantity: (itemId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(itemId);
      return;
    }
    set({
      items: get().items.map((i) =>
        i.id === itemId ? { ...i, quantity } : i
      ),
    });
  },

  removeItem: (itemId) => {
    set({ items: get().items.filter((i) => i.id !== itemId) });
  },

  updateItemModifiers: (itemId, modifiers) => {
    set({
      items: get().items.map((i) =>
        i.id === itemId ? { ...i, modifiers } : i
      ),
    });
  },

  updateItemInstructions: (itemId, specialInstructions) => {
    set({
      items: get().items.map((i) =>
        i.id === itemId ? { ...i, specialInstructions } : i
      ),
    });
  },

  updateItemCourse: (itemId, courseNumber) => {
    set({
      items: get().items.map((i) =>
        i.id === itemId ? { ...i, courseNumber } : i
      ),
    });
  },

  loadItems: (items) => {
    set({ items });
  },

  getItemsByCourse: () => {
    const items = get().items;
    const courseMap = new Map<number, OrderItem[]>();

    for (const item of items) {
      const course = item.courseNumber || 1;
      if (!courseMap.has(course)) {
        courseMap.set(course, []);
      }
      courseMap.get(course)!.push(item);
    }

    return courseMap;
  },

  getMaxCourse: () => {
    const items = get().items;
    if (items.length === 0) return 1;
    return Math.max(...items.map(i => i.courseNumber || 1));
  },

  getSubtotal: () => {
    return get().items.reduce((total, item) => {
      const itemTotal = item.priceCents * item.quantity;
      const modifiersTotal = item.modifiers.reduce(
        (sum, mod) => sum + mod.priceCents,
        0
      ) * item.quantity;
      return total + itemTotal + modifiersTotal;
    }, 0);
  },

  getTax: () => {
    return Math.round(get().getSubtotal() * get().taxRate);
  },

  getTotal: () => {
    return get().getSubtotal() + get().getTax();
  },

  clearItems: () => {
    // Only clear items, keep table/check context for reloading from server
    set({ items: [] });
  },

  clearOrder: () => {
    set({
      tableId: null,
      tableNumber: null,
      checkId: null,
      guestId: null,
      guestName: null,
      items: [],
      partySize: 1,
      currentSeat: 1,
      currentCourse: 1,
    });
  },
}));
